<template>
  <div class="m-td9">
    <!-- ==================== 概览 ==================== -->
    <div class="ov">
      <div class="ov-head">
        <span class="ov-date">{{ meta.latestDate || '—' }}</span>
        <span class="ov-cal">{{ meta.priceCaliber || '不复权' }}</span>
      </div>

      <div class="ov-mid">
        <div class="ov-cnt">
          <b class="c-down">{{ meta.counts ? meta.counts.down : '—' }}</b>
          <span>低九 · 潜在买</span>
        </div>
        <div class="ov-cnt">
          <b class="c-up">{{ meta.counts ? meta.counts.up : '—' }}</b>
          <span>高九 · 潜在卖</span>
        </div>
      </div>

      <div class="spark">
        <div v-for="(r, i) in recent" :key="r.date" class="sg">
          <div class="sbars">
            <i class="sd" :style="{ height: sparkH(r.down) }" />
            <i class="su" :style="{ height: sparkH(r.up) }" />
          </div>
          <span class="sl">{{ i === 0 ? '最新' : r.date.slice(5) }}</span>
        </div>
      </div>
      <div class="ov-note">全市场当日计数（≥ 9，含钝化期）· 近 5 个交易日</div>
    </div>

    <!-- ==================== 方向 ==================== -->
    <div class="seg">
      <button
        v-for="(d, i) in DIRS"
        :key="d.value"
        :class="['seg-i', d.value, { on: dirIdx === i }]"
        @click="setDir(i)"
      >{{ d.label }}</button>
    </div>

    <!-- ==================== 日期 / 池 ==================== -->
    <van-dropdown-menu class="dd">
      <van-dropdown-item v-model="date" :options="dateOptions" @change="onFilterChange" />
      <van-dropdown-item v-model="pool" :options="poolOptions" @change="onFilterChange" />
    </van-dropdown-menu>

    <!-- ==================== 过滤 chip ==================== -->
    <div class="chips">
      <span class="chip" :class="{ on: f1 }" @click="toggle('f1')">
        F1 非下跌趋势<i v-if="covRate('f1') !== null">{{ covRate('f1') }}%</i>
      </span>
      <span class="chip" :class="{ on: f2 }" @click="toggle('f2')">
        F2 放量 ≥1.2×<i v-if="covRate('f2') !== null">{{ covRate('f2') }}%</i>
      </span>
      <span class="chip" :class="{ on: f3 }" @click="toggle('f3')">
        F3 站上 MA5<i v-if="covRate('f3') !== null">{{ covRate('f3') }}%</i>
      </span>
      <span class="chip" :class="{ on: exactOnly }" @click="toggle('exact')">只看新出 =9</span>
    </div>

    <div class="hint">
      <span>命中 <b>{{ rows.length }}</b> 条</span>
      <span v-if="baseRows.length !== rows.length" class="muted">／基线 {{ baseRows.length }}</span>
      <span v-if="comboCov !== null" class="muted">· 三叠加 {{ comboCov }}%</span>
      <span class="muted">· 排序：方向 → 计数 → 量比</span>
    </div>

    <!-- ==================== 列表 ==================== -->
    <van-empty v-if="error" :description="error" />
    <van-loading v-else-if="loading && !rows.length" class="pl" vertical>正在计算九转信号…</van-loading>
    <van-empty v-else-if="!loading && !rows.length" :description="emptyText" />

    <template v-else>
      <div
        v-for="(r, i) in pagedRows"
        :key="r.code + r.dir"
        class="card"
        @click="openChart(r)"
      >
        <div class="c-head">
          <span class="c-rank">#{{ (page - 1) * pageSize + i + 1 }}</span>
          <span class="c-tag" :class="r.dir">{{ r.dir === 'down' ? '低九·买' : '高九·卖' }} {{ r.count }}</span>
          <a class="c-code" :href="stockstarCodeLink(r.code)" target="_blank" rel="noopener noreferrer" @click.stop>{{ r.code }}</a>
          <a class="c-name" :href="eastmoneyCodeLink(r)" target="_blank" rel="noopener noreferrer" @click.stop>{{ r.name || '—' }}</a>
          <span class="c-chg" :class="pctCls(r.changePct)">{{ signed(r.changePct) }}%</span>
        </div>

        <div class="c-meta">
          <span>{{ r.industry || '—' }}</span>
          <span class="c-close">{{ fmtNum(r.close) }}</span>
          <span :class="{ hl: r.volRatio != null && r.volRatio >= 1.2 }">
            量比 {{ r.volRatio == null ? '—' : r.volRatio.toFixed(2) }}
          </span>
          <span :class="numCls(r.ma5Dist)">MA5 {{ signed(r.ma5Dist) }}%</span>
          <span>{{ r.cap == null ? '—' : r.cap.toFixed(0) + ' 亿' }}</span>
        </div>

        <div class="c-flags">
          <i v-if="r.f1Pass" class="fl">F1</i>
          <i v-if="r.f2Pass" class="fl">F2</i>
          <i v-if="r.f3" class="fl">F3</i>
          <i v-if="r.exDiv" class="fl fl-warn">除权</i>
          <span class="c-open">看 K 线 ›</span>
        </div>
      </div>

      <div v-if="pageCount > 1" class="pager">
        <van-pagination
          v-model="page"
          :total-items="rows.length"
          :items-per-page="pageSize"
          force-ellipses
        />
      </div>
    </template>

    <!-- ==================== 口径折叠 ==================== -->
    <div class="fd">
      <div class="fd-head" @click="showFoot = !showFoot">
        <span>口径与局限</span>
        <van-icon :name="showFoot ? 'arrow-up' : 'arrow-down'" />
      </div>
      <div v-show="showFoot" class="fd-body">
        <p><b>信号定义</b>：低九 = 连续 9 根 <code>close &lt; close[−4]</code>，高九反之；中断归零，越过 9 后不重置，继续 10/11/12…</p>
        <p><b>复权口径</b>：<b>不复权</b>（daily_bar），与东财盘面九转同口径；除权跳空可能造假信号，已用 <code>pre_close ≠ 前收</code> 检测并打「除权」标。</p>
        <p><b>可回溯</b>：最近 {{ dateOptions.length }} 个交易日。TD Sequential 的 Countdown 段（13 计数）未实现。</p>
        <p class="warn"><b>F1 / F2 / F3 不是「勾上更准」</b>：报告第五章实测（2015 年以来全部低九信号、持有 3 日毛均值）显示 F2 放量是唯一显著正向，F1 反而有害（站上 MA20 −0.048% / ADX&lt;25 −0.297%），三者叠加毛均值转负（−0.385%）且样本只剩 1.1%。因此默认全不勾。</p>
        <p class="muted">本页只做信号查找，不做收益展示与交易口径。数据源刻意用 daily_bar（不复权）而非 market_daily_hfq —— 后者线上为空。</p>
      </div>
    </div>

    <!-- ==================== K 线弹层 ==================== -->
    <van-popup
      v-model="showChart"
      position="bottom"
      round
      :style="{ height: '78%' }"
      @opened="paintChart"
    >
      <div class="k">
        <div class="k-head">
          <div class="k-titles">
            <b>{{ chart ? chart.code : chartCode }} {{ chart ? chart.name : '' }}</b>
            <span class="k-sub">{{ kSub }}</span>
          </div>
          <van-icon name="cross" class="k-close" @click="showChart = false" />
        </div>

        <div v-if="chartLoading" class="k-state"><van-loading vertical>加载 K 线…</van-loading></div>
        <div v-else-if="chartError" class="k-state k-err">{{ chartError }}</div>
        <div v-else-if="chart" class="k-body">
          <div ref="chart" class="k-box" />

          <div class="k-legend">
            <span><i class="dot d" />低九计数</span>
            <span><i class="dot u" />高九计数</span>
            <span><i class="ln m5" />MA5</span>
            <span><i class="ln m20" />MA20</span>
            <span class="k-warn" v-if="chart.exDates && chart.exDates.length">
              除权日 {{ chart.exDates.length }} 个
            </span>
          </div>

          <div class="k-kv">
            <div class="kv-i">
              <span>当前低九计数</span>
              <b :class="chartLast.down >= 9 ? 'hl' : ''">{{ chartLast.down }}</b>
            </div>
            <div class="kv-i">
              <span>当前高九计数</span>
              <b :class="chartLast.up >= 9 ? 'hl' : ''">{{ chartLast.up }}</b>
            </div>
            <div class="kv-i">
              <span>量比（20 日）</span>
              <b>{{ currentRow && currentRow.volRatio != null ? currentRow.volRatio.toFixed(2) : '—' }}</b>
            </div>
            <div class="kv-i">
              <span>MA5 乖离</span>
              <b :class="currentRow ? numCls(currentRow.ma5Dist) : ''">{{ currentRow ? signed(currentRow.ma5Dist) + '%' : '—' }}</b>
            </div>
            <div class="kv-i">
              <span>流通市值</span>
              <b>{{ currentRow && currentRow.cap != null ? currentRow.cap.toFixed(0) + ' 亿' : '—' }}</b>
            </div>
            <div class="kv-i">
              <span>行业</span>
              <b class="kv-w">{{ currentRow && currentRow.industry ? currentRow.industry : '—' }}</b>
            </div>
          </div>

          <p class="k-foot">
            K 线 {{ chart.bars.length }} 根 · 红涨绿跌（A 股惯例）· 口径：{{ chart.priceCaliber }}
          </p>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { CandlestickChart, LineChart, BarChart, ScatterChart } from 'echarts/charts'
import { GridComponent, TooltipComponent, AxisPointerComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { request } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

echarts.use([
  CandlestickChart, LineChart, BarChart, ScatterChart,
  GridComponent, TooltipComponent, AxisPointerComponent, CanvasRenderer,
])

const DIRS = [
  { value: 'all', label: '全部' },
  { value: 'down', label: '低九 · 买' },
  { value: 'up', label: '高九 · 卖' },
]
const POOL_LABELS = { all: '全市场', top500: 'Top500', p75: 'P75 机构池' }

export default {
  name: 'MobileTD9',
  data() {
    return {
      DIRS,
      meta: {},
      dirIdx: 0,
      date: '',
      pool: 'all',
      f1: false,
      f2: false,
      f3: false,
      exactOnly: false,
      rows: [],
      baseRows: [],
      loading: false,
      error: '',
      page: 1,
      pageSize: 20,
      showFoot: false,
      // K 线弹层
      showChart: false,
      chartCode: '',
      chart: null,
      chartLoading: false,
      chartError: '',
      currentRow: null,
      instance: null,
      requestSeq: 0,
    }
  },
  computed: {
    recent() {
      return this.meta.recent || []
    },
    dir() {
      return DIRS[this.dirIdx].value
    },
    dateOptions() {
      return (this.meta.dates || []).map((d) => ({
        text: d === this.meta.latestDate ? `${d}（最新）` : d,
        value: d,
      }))
    },
    poolOptions() {
      const counts = this.meta.pools || {}
      return ['all', 'top500', 'p75'].map((value) => {
        const n = counts[value]
        return { text: n != null ? `${POOL_LABELS[value]} ${n}` : POOL_LABELS[value], value }
      })
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.rows.length / this.pageSize))
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize
      return this.rows.slice(start, start + this.pageSize)
    },
    hasFilter() {
      return this.f1 || this.f2 || this.f3
    },
    emptyText() {
      if (this.dir === 'down') return '当前条件下无低九信号（试试切全市场池）'
      if (this.dir === 'up') return '当前条件下无高九信号'
      return '当前条件下无九转信号'
    },
    /** 三叠加：只在有基线时才算（分母是基线，不看当前勾选） */
    comboCov() {
      const base = this.baseRows
      if (!base.length) return null
      const n = base.filter((r) => r.f1Pass && r.f2Pass && r.f3).length
      return ((n / base.length) * 100).toFixed(1)
    },
    chartLast() {
      const c = this.chart
      if (!c) return { down: 0, up: 0 }
      const i = (c.bars || []).length - 1
      if (i < 0) return { down: 0, up: 0 }
      return { down: (c.down || [])[i] || 0, up: (c.up || [])[i] || 0 }
    },
    kSub() {
      const r = this.currentRow
      if (!r) return ''
      const dirTxt = r.dir === 'down' ? '低九' : '高九'
      const phase = r.count > 9 ? '钝化期' : '第 9 根成立'
      return `${r.industry || '—'} · ${dirTxt}计数 ${r.count}（${phase}）· 信号日 ${this.date}`
    },
  },
  watch: {
    f1() { this.loadSignals() },
    f2() { this.loadSignals() },
    f3() { this.loadSignals() },
    exactOnly() { this.loadSignals() },
    showChart(val) {
      // 关闭弹层时释放 canvas，避免多次开关累积实例
      if (!val) this.disposeChart()
    },
  },
  created() {
    this.loadMeta()
  },
  beforeDestroy() {
    this.disposeChart()
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,

    /* ---------------- 数据 ---------------- */
    async loadMeta() {
      try {
        const meta = await request('/api/td9/meta')
        this.meta = meta || {}
        this.date = meta.latestDate || ''
        await this.loadSignals()
      } catch (err) {
        this.error = err.message || '加载九转元数据失败'
      }
    },
    async loadSignals() {
      if (!this.date) return
      const seq = ++this.requestSeq
      this.loading = true
      this.error = ''
      try {
        // 基线：date + dir + pool + exact，不含 f1/f2/f3
        const baseQs = new URLSearchParams({ date: this.date, dir: this.dir, pool: this.pool })
        if (this.exactOnly) baseQs.set('exact', '1')
        const baseUrl = `/api/td9/signals?${baseQs.toString()}`

        // 叠加 F 过滤的请求
        const filterQs = new URLSearchParams(baseQs.toString())
        if (this.f1) filterQs.set('f1', '1')
        if (this.f2) filterQs.set('f2', '1')
        if (this.f3) filterQs.set('f3', '1')

        // 未勾任何 F 时两者等价，只发一次；勾了才多发一次基线（用于算真实覆盖率分母）
        const tasks = this.hasFilter
          ? [request(`/api/td9/signals?${filterQs.toString()}`), request(baseUrl)]
          : [request(baseUrl)]
        const [filtered, baseline] = await Promise.all(tasks)
        if (seq !== this.requestSeq) return

        this.rows = filtered.rows || []
        this.baseRows = (this.hasFilter ? (baseline || {}).rows : filtered.rows) || []
        if (this.page > this.pageCount) this.page = 1
      } catch (err) {
        if (seq !== this.requestSeq) return
        this.rows = []
        this.baseRows = []
        this.error = err.message || '加载信号列表失败'
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    onFilterChange() {
      this.page = 1
      this.loadSignals()
    },
    setDir(i) {
      if (this.dirIdx === i) return
      this.dirIdx = i
      this.onFilterChange()
    },
    toggle(key) {
      if (key === 'exact') this.exactOnly = !this.exactOnly
      else this[key] = !this[key]
      this.page = 1
      // 其余交给 watch 触发 loadSignals，避免重复请求
    },
    /** 从基线里数出单个过滤器的通过率（基线含 dir/pool/date/exact，不含 f1/f2/f3） */
    covRate(key) {
      const base = this.baseRows
      if (!base.length) return null
      const pass = base.filter((r) => (key === 'f1' ? r.f1Pass : key === 'f2' ? r.f2Pass : r.f3)).length
      return ((pass / base.length) * 100).toFixed(0)
    },

    /* ---------------- K 线弹层 ---------------- */
    openChart(row) {
      this.currentRow = row
      this.chartCode = row.code
      this.showChart = true
      this.loadChart()
    },
    async loadChart() {
      if (!this.chartCode) return
      this.chartLoading = true
      this.chartError = ''
      try {
        this.chart = await request(`/api/td9/chart?code=${this.chartCode}`)
      } catch (err) {
        this.chartError = err.message || '加载 K 线失败'
        this.chart = null
        this.disposeChart()
      } finally {
        this.chartLoading = false
        // 若弹层已开（@opened 早于数据返回），数据到手后再画一次
        if (this.chart && this.showChart) this.$nextTick(() => this.renderChart())
      }
    },
    paintChart() {
      this.$nextTick(() => this.renderChart())
    },
    disposeChart() {
      if (this.instance) {
        this.instance.dispose()
        this.instance = null
      }
    },
    renderChart() {
      const el = this.$refs.chart
      const c = this.chart
      if (!el || !c || !(c.bars || []).length) return
      if (!this.instance) this.instance = echarts.init(el, null, { renderer: 'canvas' })
      const bars = c.bars
      const dates = bars.map((b) => b.date)
      const ohlc = bars.map((b) => [b.open, b.close, b.low, b.high])
      const ma5 = (c.ma5 || []).map((v) => (v == null ? null : v))
      const ma20 = (c.ma20 || []).map((v) => (v == null ? null : v))
      const down = (c.down || []).map((v) => (v >= 1 ? v : null))
      const up = (c.up || []).map((v) => (v >= 1 ? -v : null))
      const exPts = (c.exDates || []).map((d) => {
        const i = dates.indexOf(d)
        return i < 0 ? null : { coord: [dates[i], bars[i].low], value: '除' }
      }).filter(Boolean)

      this.instance.setOption({
        animation: false,
        grid: [
          { left: 40, right: 12, top: 16, height: '52%' },
          { left: 40, right: 12, top: '74%', height: '18%' },
        ],
        axisPointer: { link: [{ xAxisIndex: 'all' }] },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross' },
          confine: true,
          textStyle: { fontSize: 11 },
        },
        xAxis: [
          {
            type: 'category',
            data: dates,
            boundaryGap: true,
            axisLine: { lineStyle: { color: '#dcdee0' } },
            axisLabel: { show: false },
            axisTick: { show: false },
          },
          {
            type: 'category',
            gridIndex: 1,
            data: dates,
            boundaryGap: true,
            axisLine: { lineStyle: { color: '#dcdee0' } },
            axisLabel: { fontSize: 9, color: '#969799', interval: Math.floor(dates.length / 5) },
            axisTick: { show: false },
          },
        ],
        yAxis: [
          {
            scale: true,
            splitLine: { lineStyle: { color: '#f2f3f5' } },
            axisLabel: { fontSize: 9, color: '#969799' },
          },
          {
            gridIndex: 1,
            min: -12,
            max: 12,
            interval: 6,
            splitLine: { show: false },
            axisLabel: { fontSize: 9, color: '#969799', formatter: (v) => Math.abs(v) },
          },
        ],
        series: [
          {
            name: 'K线',
            type: 'candlestick',
            data: ohlc,
            itemStyle: {
              color: '#ee0a24',
              color0: '#07c160',
              borderColor: '#ee0a24',
              borderColor0: '#07c160',
            },
          },
          {
            name: 'MA5',
            type: 'line',
            data: ma5,
            symbol: 'none',
            lineStyle: { width: 1, color: '#ff976a' },
            smooth: true,
          },
          {
            name: 'MA20',
            type: 'line',
            data: ma20,
            symbol: 'none',
            lineStyle: { width: 1, color: '#1989fa' },
            smooth: true,
          },
          {
            name: '除权',
            type: 'scatter',
            data: exPts,
            symbolSize: 13,
            itemStyle: { color: '#e6a23c' },
            label: { show: true, formatter: '除', fontSize: 8, color: '#fff' },
          },
          {
            name: '计数',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: down.map((v) => ({ value: v, itemStyle: { color: '#ee0a24' } })),
            barWidth: '55%',
          },
          {
            name: '计数',
            type: 'bar',
            xAxisIndex: 1,
            yAxisIndex: 1,
            data: up.map((v) => ({ value: v, itemStyle: { color: '#07c160' } })),
            barWidth: '55%',
          },
        ],
      }, true)
      this.instance.resize()
    },

    /* ---------------- 展示助手 ---------------- */
    sparkH(v) {
      const list = this.recent || []
      const max = Math.max(1, ...list.map((x) => Math.max(x.down || 0, x.up || 0)))
      return `${Math.max(3, Math.round(((Number(v) || 0) / max) * 26))}px`
    },
    fmtNum(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    signed(v) {
      if (v === null || v === undefined || !Number.isFinite(Number(v))) return '—'
      const n = Number(v)
      return `${n >= 0 ? '+' : ''}${n.toFixed(2)}`
    },
    pctCls(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return 'muted'
      return n > 0 ? 'up' : 'down'
    },
    numCls(v) {
      const n = Number(v)
      if (!Number.isFinite(n) || n === 0) return 'muted'
      return n > 0 ? 'up' : 'down'
    },
  },
}
</script>

<style scoped>
.m-td9 {
  padding-bottom: 16px;
}

/* ---------- 概览 ---------- */
.ov {
  margin: 10px 12px 8px;
  padding: 12px 14px 10px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.ov-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.ov-date {
  font-size: 15px;
  font-weight: 600;
  color: #323233;
}
.ov-cal {
  font-size: 11px;
  color: #969799;
}
.ov-mid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}
.ov-cnt {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 0;
  background: #f7f8fa;
  border-radius: 8px;
}
.ov-cnt b {
  font-size: 22px;
  line-height: 1.15;
}
.ov-cnt span {
  font-size: 11px;
  color: #646566;
  margin-top: 2px;
}
.c-down { color: #ee0a24; }
.c-up { color: #07c160; }

.spark {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 4px;
  margin-top: 10px;
  align-items: end;
}
.sg {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.sbars {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  height: 28px;
}
.sd, .su {
  width: 7px;
  border-radius: 2px 2px 0 0;
  display: block;
}
.sd { background: #ee0a24; }
.su { background: #07c160; }
.sl {
  margin-top: 3px;
  font-size: 10px;
  color: #969799;
}
.ov-note {
  margin-top: 8px;
  font-size: 11px;
  color: #969799;
  line-height: 1.5;
}

/* ---------- 方向段控 ---------- */
.seg {
  display: flex;
  gap: 6px;
  margin: 0 12px 8px;
}
.seg-i {
  flex: 1;
  padding: 8px 0;
  font-size: 13px;
  color: #646566;
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 8px;
  font-family: inherit;
}
.seg-i.on.down {
  background: #fff1f0;
  border-color: #ee0a24;
  color: #ee0a24;
  font-weight: 600;
}
.seg-i.on.up {
  background: #f0fff5;
  border-color: #07c160;
  color: #07c160;
  font-weight: 600;
}
.seg-i.on.all {
  background: #ecf5ff;
  border-color: #1989fa;
  color: #1989fa;
  font-weight: 600;
}

/* ---------- 下拉 ---------- */
.dd {
  margin-bottom: 8px;
}

/* ---------- chip ---------- */
.chips {
  display: flex;
  gap: 6px;
  padding: 0 12px 6px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.chips::-webkit-scrollbar { display: none; }
.chip {
  flex: none;
  padding: 5px 10px;
  font-size: 12px;
  color: #646566;
  background: #fff;
  border: 1px solid #ebedf0;
  border-radius: 14px;
  white-space: nowrap;
}
.chip i {
  font-style: normal;
  margin-left: 4px;
  font-size: 10px;
  color: #969799;
}
.chip.on {
  background: #ecf5ff;
  border-color: #1989fa;
  color: #1989fa;
}
.chip.on i { color: #1989fa; }

.hint {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 6px;
  padding: 0 12px 8px;
  font-size: 11px;
  color: #323233;
}
.hint b { color: #323233; }
.muted { color: #969799; }

/* ---------- 卡片 ---------- */
.pl { padding: 48px 0; }
.card {
  margin: 8px 12px;
  padding: 10px 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}
.c-head {
  display: flex;
  align-items: center;
  gap: 6px;
}
.c-rank {
  font-size: 11px;
  color: #c8c9cc;
  min-width: 24px;
}
.c-tag {
  flex: none;
  padding: 1px 6px;
  font-size: 11px;
  border-radius: 4px;
  font-weight: 600;
}
.c-tag.down {
  color: #ee0a24;
  background: #fff1f0;
}
.c-tag.up {
  color: #07c160;
  background: #f0fff5;
}
.c-code {
  font-size: 12px;
  color: #969799;
  text-decoration: none;
}
.c-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 600;
  color: #1989fa;
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.c-chg {
  font-size: 13px;
  font-weight: 600;
}
.c-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  margin-top: 6px;
  font-size: 12px;
  color: #646566;
}
.c-close { color: #323233; }
.hl { color: #e6a23c; font-weight: 600; }
.c-flags {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 7px;
}
.fl {
  font-style: normal;
  padding: 1px 5px;
  font-size: 10px;
  color: #1989fa;
  background: #ecf5ff;
  border-radius: 3px;
}
.fl-warn {
  color: #e6a23c;
  background: #fdf6ec;
}
.c-open {
  margin-left: auto;
  font-size: 11px;
  color: #c8c9cc;
}
.pager {
  padding: 12px 8px 4px;
}

.up { color: #ee0a24; }
.down { color: #07c160; }

/* ---------- 口径折叠 ---------- */
.fd {
  margin: 14px 12px 0;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
}
.fd-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #323233;
}
.fd-body {
  padding: 0 12px 12px;
  font-size: 12px;
  color: #646566;
  line-height: 1.65;
}
.fd-body p { margin: 0 0 7px; }
.fd-body code {
  background: #f2f3f5;
  padding: 0 3px;
  border-radius: 3px;
  font-size: 11px;
}
.fd-body .warn { color: #e6a23c; }
.fd-body .muted { color: #969799; }

/* ---------- K 线弹层 ---------- */
.k {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
}
.k-head {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 14px 14px 8px;
  border-bottom: 1px solid #f2f3f5;
}
.k-titles {
  flex: 1;
  min-width: 0;
}
.k-titles b {
  font-size: 15px;
  color: #323233;
}
.k-sub {
  display: block;
  margin-top: 3px;
  font-size: 11px;
  color: #969799;
  line-height: 1.5;
}
.k-close {
  font-size: 18px;
  color: #969799;
  padding: 2px;
}
.k-state { padding: 60px 0; }
.k-err { text-align: center; color: #ee0a24; font-size: 13px; }
.k-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.k-box {
  width: 100%;
  height: 300px;
  flex: none;
}
.k-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding: 6px 14px;
  font-size: 11px;
  color: #646566;
}
.k-legend i {
  display: inline-block;
  vertical-align: middle;
  margin-right: 3px;
}
.dot {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}
.dot.d { background: #ee0a24; }
.dot.u { background: #07c160; }
.ln {
  width: 12px;
  height: 2px;
}
.ln.m5 { background: #ff976a; }
.ln.m20 { background: #1989fa; }
.k-warn { color: #e6a23c; }
.k-kv {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  padding: 8px 14px 16px;
  align-content: start;
}
.kv-i {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background: #f7f8fa;
  border-radius: 8px;
}
.kv-i span {
  font-size: 11px;
  color: #969799;
}
.kv-i b {
  font-size: 14px;
  color: #323233;
}
.kv-w {
  font-size: 12px !important;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.k-foot {
  margin: 0;
  padding: 0 14px 16px;
  font-size: 11px;
  color: #969799;
}
</style>
